import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FileText, Sparkles, Clock, CheckCircle, AlertCircle, Search as SearchIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const STATUS_STYLES = {
  Draft: 'bg-warning/10 text-warning border-warning/20',
  In_Progress: 'bg-info/10 text-info border-info/20',
  Completed: 'bg-success/10 text-success border-success/20',
};

const STATUS_ICONS = {
  Draft: Clock,
  In_Progress: Sparkles,
  Completed: CheckCircle,
};

const CHANNEL_LABELS = {
  Paid_Search: 'Paid Search',
  Paid_Social: 'Paid Social',
  Programmatic: 'Programmatic',
  Influencer: 'Influencers',
  SEO: 'SEO',
};

const WorkflowsHub = ({ user }) => {
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const loadBriefs = async () => {
      try {
        
        const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs`, {
        });
        if (response.ok) {
          const data = await response.json();
          setBriefs(data.briefs);
        } else {
          toast.error('Failed to load briefs');
        }
      } catch (e) {
        toast.error('Network error loading briefs');
      } finally {
        setLoading(false);
      }
    };
    loadBriefs();
     
  }, []);

  const fetchBriefs = async () => {
    try {
      
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs`, {
      });
      if (response.ok) {
        const data = await response.json();
        setBriefs(data.briefs);
      } else {
        toast.error('Failed to load briefs');
      }
    } catch (e) {
      toast.error('Network error loading briefs');
    } finally {
      setLoading(false);
    }
  };

  const filtered = briefs.filter(b => {
    const matchesSearch =
      (b.objective || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.industry_vertical || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.channel || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || b.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    all: briefs.length,
    Draft: briefs.filter(b => b.status === 'Draft').length,
    In_Progress: briefs.filter(b => b.status === 'In_Progress').length,
    Completed: briefs.filter(b => b.status === 'Completed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-up" data-testid="workflows-hub">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight text-text-primary mb-1">Campaign Workflows</h2>
          <p className="text-text-tertiary text-sm">AI-powered campaign briefs organized by channel</p>
        </div>
        <Button
          onClick={() => navigate('/internal/workflows/briefs/create')}
          className="bg-brand hover:bg-brand-hover text-white gap-2"
          data-testid="create-new-brief-btn"
        >
          <Plus size={18} />
          New Brief
        </Button>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="status-filters">
        {Object.entries({ all: 'All', Draft: 'Draft', In_Progress: 'In Progress', Completed: 'Completed' }).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilterStatus(key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filterStatus === key
                ? 'bg-brand text-white'
                : 'bg-white/60 text-text-secondary hover:bg-white'
            }`}
            data-testid={`filter-${key}`}
          >
            {label} ({statusCounts[key] || 0})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
        <Input
          placeholder="Search briefs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="briefs-search"
        />
      </div>

      {/* Briefs List */}
      {filtered.length === 0 ? (
        <Card className="glass">
          <CardContent className="p-12 text-center">
            <FileText size={48} className="mx-auto text-text-muted mb-4" />
            <h3 className="text-lg font-semibold text-text-secondary mb-2">
              {briefs.length === 0 ? 'No briefs yet' : 'No matching briefs'}
            </h3>
            <p className="text-text-tertiary mb-6">
              {briefs.length === 0
                ? 'Create your first campaign brief to get started.'
                : 'Try adjusting your search or filters.'}
            </p>
            {briefs.length === 0 && (
              <Button
                onClick={() => navigate('/internal/workflows/briefs/create')}
                className="bg-brand hover:bg-brand-hover text-white gap-2"
              >
                <Plus size={18} />
                Create First Brief
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="briefs-list">
          {filtered.map((brief) => {
            const StatusIcon = STATUS_ICONS[brief.status] || AlertCircle;
            return (
              <Link
                key={brief.id}
                to={`/internal/workflows/briefs/${brief.id}`}
                className="block"
                data-testid={`brief-${brief.id}`}
              >
                <Card className="glass hover:bg-white/70 transition-all hover:-translate-y-0.5 cursor-pointer">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand to-brand-hover flex items-center justify-center flex-shrink-0">
                          <FileText className="text-white" size={20} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-text-primary truncate">
                              {brief.objective} — {CHANNEL_LABELS[brief.channel] || brief.channel}
                            </h3>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${STATUS_STYLES[brief.status] || 'bg-gray-100 text-gray-600'}`}>
                              {brief.status?.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-text-tertiary">
                            <span>{brief.industry_vertical?.replace('_', ' ')}</span>
                            {brief.budget_total && <span>${Number(brief.budget_total).toLocaleString()}</span>}
                            {brief.start_date && <span>Start: {brief.start_date}</span>}
                            {brief.platforms?.length > 0 && (
                              <span>{brief.platforms.map(p => p.replace('_', ' ')).join(', ')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                        {brief.ai_generated_campaigns && (
                          <span className="px-2 py-1 rounded-md bg-brand/10 text-brand text-xs font-medium flex items-center gap-1">
                            <Sparkles size={12} />
                            AI Generated
                          </span>
                        )}
                        <span className="text-xs text-text-tertiary">
                          {brief.created_at ? new Date(brief.created_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkflowsHub;
